/* eslint-disable */
import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import CSSModules from 'react-css-modules';
import classNames from 'classnames';
import autosize from 'autosize';
import omit from 'lodash/omit';

import { PROPS_LIST } from '../../constants/props';
import { INPUT_HEIGHT } from '../../constants/styles';
import { isIE } from '../../utils/browserChecker';
import styles from './index.styl';
import { formatValue } from './utils';
import { parseDigits } from '../../utils/mask/phone';

@CSSModules(styles, { allowMultiple: true })
export default class TextInput extends React.Component {
  static propTypes = {
    autoresize: PropTypes.bool,
    children: PropTypes.any,
    className: PropTypes.string,
    color: PropTypes.oneOf(PROPS_LIST.textInput.color),
    disabled: PropTypes.bool,
    id: PropTypes.string,
    invalid: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    limit: PropTypes.number,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyPress: PropTypes.func,
    placeholder: PropTypes.string,
    rounded: PropTypes.bool,
    isPhoneNumberField: PropTypes.bool,
    src: PropTypes.string,
    style: PropTypes.object,
    shouldUpperCase: PropTypes.bool,
    wrapperStyle: PropTypes.object,
    textarea: PropTypes.bool,
    type: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
    ]),
    maxheight: PropTypes.number,
    useNumberHeight: PropTypes.bool,
    isFractionNumber: PropTypes.bool,
    step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shouldClearWhenOnFocus: PropTypes.bool,
    withTrimMode: PropTypes.bool,
  };

  static defaultProps = {
    color: 'default',
    disabled: false,
    invalid: false,
    limit: Infinity,
    type: 'text',
  };

  state = {
    focused: false,
    value: '',
  };

  componentWillMount() {
    this.setValue(this.props.value);
    const overflowed = this.props.maxheight && isIE();
    this.setState({ overflowed });
  }

  componentDidMount() {
    /*
     * Setting up the autoresize for textarea
     * */
    const { textarea, autoresize } = this.props;
    if (textarea && autoresize) {
      autosize(this.node);
      this.updateSize(this.props);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { autoresize, textarea } = this.props;
    if (autoresize && textarea) {
      this.setState(
        {
          value: nextProps.value,
        },
        () => {
          this.updateSize(nextProps);
        },
      );
    }

    if (this.props.value !== nextProps.value) {
      this.setValue(nextProps.value);
    }
  }

  componentWillUnmount() {
    autosize.destroy(this.node);
  }

  @autobind
  handleChange(event) {
    /*
     * Do not let input more than `limit` prop
     * */
    const {
      limit,
      onChange,
      maxheight,
      min,
      max,
      type,
      withTrimMode,
      shouldUpperCase,
      isPhoneNumberField,
    } = this.props;
    const val = event.target.value;
    const value =
      limit < Infinity && event.target.value.length > limit
        ? val.substr(0, limit)
        : val;

    if (type === 'number' && (min || max)) {
      const floatValue = parseFloat(val);
      if ((min && floatValue < min) || (max && floatValue > max)) {
        return;
      }
    }

    if (withTrimMode) {
      const trimmedValue = isPhoneNumberField
        ? parseDigits(value)
        : value.trim();
      this.setState({ value: trimmedValue });
      onChange && onChange(trimmedValue);
    } else if (shouldUpperCase) {
      const upperCasedValue = isPhoneNumberField
        ? parseDigits(value)
        : value.toUpperCase();
      this.setState({ value: upperCasedValue });
      onChange &&
        onChange(isPhoneNumberField ? parseDigits(value) : upperCasedValue);
    } else {
      const fieldValue = isPhoneNumberField ? parseDigits(value) : value;
      this.setState({ value: fieldValue });
      onChange && onChange(fieldValue);
    }

    // avoid scroll bar overflow important message icon
    const overflowed =
      maxheight && (this.node.clientHeight > maxheight || isIE());
    this.setState({ overflowed });
  }

  @autobind
  handleFocus(event) {
    event.persist();
    const { onFocus, shouldClearWhenOnFocus } = this.props;

    this.setState(
      state => ({
        focused: !state.focused,
        ...(shouldClearWhenOnFocus && { value: '' }),
      }),
      () => {
        if (onFocus) onFocus(this.state.focused, event);
      },
    );
  }

  @autobind
  handleBlur(e) {
    const { onBlur } = this.props;
    const value = e.target.value;

    this.setState({ focused: false });
    if (onBlur) onBlur(value);
  }

  @autobind
  setValue(value) {
    let existingValue = '';

    if (value) {
      existingValue = typeof value === 'string' ? value : value.join(', ');
    }

    this.setState({
      value: existingValue,
    });
  }

  @autobind
  handleKeyPress(e) {
    const { type, isFractionNumber, onKeyPress } = this.props;
    if (type === 'number' && !isFractionNumber) {
      return this.filterAlphabetical(e);
    }

    onKeyPress && onKeyPress(e);
  }

  filterAlphabetical(e) {
    const { charCode } = e.nativeEvent;

    if (charCode < 48 || charCode > 57) {
      e.preventDefault();
    }
  }

  updateSize(props) {
    if (props.value && props.value.length) {
      autosize.update(this.node);
    } else {
      const initialHeight = INPUT_HEIGHT * (parseInt(this.props.rows, 10) || 1);
      this.node.style.height = this.props.useNumberHeight
        ? initialHeight
        : `${initialHeight}px`;
    }
  }

  render() {
    const {
      children,
      className,
      color,
      disabled,
      id,
      invalid,
      label,
      placeholder,
      rounded,
      src,
      style,
      wrapperStyle,
      textarea,
      type,
      isPhoneNumberField,
      autoresize,
      limit,
      value,
      useNumberHeight,
      isFractionNumber,
      step,
      setRef,
      required,
      shouldUpperCase,
      withTrimMode,
      ...other
    } = this.props;

    const ComponentTag = textarea ? 'textarea' : 'input';

    const isNumberFieldType = type === 'number';

    return (
      <div
        className={classNames(className, {
          'textarea--overflowed': textarea && this.state.overflowed,
        })}
        styleName={classNames('root', {
          [`root--color--${color}`]: color,
          'root--disabled': disabled,
          'root--invalid': invalid,
          'root--focused': this.state.focused,
        })}
        style={wrapperStyle}
      >
        <If condition={label}>
          <label
            styleName={classNames('label', { required })}
            className="dib mb1"
            htmlFor={id}
            children={label}
          />
        </If>
        <div className="relative">
          <ComponentTag
            {...{ disabled, id, placeholder }}
            type={isPhoneNumberField ? 'text' : type}
            {...omit(other, [
              'inputHeight',
              'shouldClearWhenOnFocus',
              'min',
              'max',
            ])}
            styleName={classNames(
              'input',
              { 'input--icon': src },
              { 'input--rounded': rounded },
            )}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            onKeyPress={this.handleKeyPress}
            style={Object.assign(
              {},
              src && { backgroundImage: `url(${src})` },
              style,
            )}
            ref={
              setRef ||
              (node => {
                this.node = node;
              })
            }
            value={formatValue(
              this.state.value,
              isNumberFieldType,
              !!isPhoneNumberField,
            )}
            step={step}
          />
          <If condition={children}>{children}</If>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/TextInput/index.jsx